import { HeroUIProvider } from '@heroui/system'
import { QueryProvider } from '@qse/api'
import { useCrossTabLogout } from '@qse/auth-ui'
import { SentryErrorBoundary, useSentryUser } from '@qse/sentry'
import { ModalComponent, ThemeProvider, Toaster, useUploadPersistence } from '@qse/ui'
import { useGlobalFolderDeletionListener } from '@qse/utils'
import type { NavigateOptions } from 'react-router-dom'
import { useHref, useNavigate } from 'react-router-dom'

import { useDriveRealtime } from './hooks'

declare module '@react-types/shared' {
  interface RouterConfig {
    routerOptions: NavigateOptions
  }
}

function RealtimeEffects() {
  // Use the new drive-specific realtime hook
  useDriveRealtime()

  // Listen for folder deletion events across all tabs
  useGlobalFolderDeletionListener()

  // Sync logout across browser tabs
  useCrossTabLogout()

  // Sync user context with Sentry
  useSentryUser()

  return null
}

function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const isUploadReady = useUploadPersistence()

  return (
    <>
      <RealtimeEffects />
      {!isUploadReady ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Preparing your workspace...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </>
  )
}

function HeroUIProviderWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()

  return (
    <HeroUIProvider navigate={navigate} useHref={useHref}>
      {children}
    </HeroUIProvider>
  )
}

export function Provider({ children }: { children: React.ReactNode }) {
  return (
    <SentryErrorBoundary>
      <HeroUIProviderWrapper>
        <ThemeProvider>
          <QueryProvider>
            <RealtimeProvider>
              {children}
              <ModalComponent />
              <Toaster />
            </RealtimeProvider>
          </QueryProvider>
        </ThemeProvider>
      </HeroUIProviderWrapper>
    </SentryErrorBoundary>
  )
}
